/**
 * EventForm Component
 *
 * Pure form component for creating/editing calendar events.
 * Supports 3 event types:
 * - Event: Pure events (live/telematic), with recurrence and participant reminders
 * - Reminder: Linked to a task, follows task recurrence
 * - Milestone: Linked to a project, groups tasks
 */

import { useState, useEffect, useMemo } from 'react';
import {
  useProjectsStore,
  useHubStore,
  type CalendarEvent,
} from '@business-assistant/shared';
import { useTranslation } from '../../hooks/useTranslation';
import {
  Calendar,
  Video,
  Phone,
  MapPin,
  Users,
  Bell,
  Repeat,
  Link as LinkIcon,
  Target,
  CheckSquare,
  Plus,
  X,
  ChevronDown,
} from 'lucide-react';

// =============================================================================
// TYPES
// =============================================================================

export type EventType = 'event' | 'reminder' | 'milestone';
export type EventMode = 'live' | 'call' | 'videocall';
export type NotificationChannel = 'email' | 'whatsapp' | 'telegram' | 'sms' | 'push';
export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';

export interface ReminderConfig {
  channel: NotificationChannel;
  offsetMinutes: number;
}

export interface EventParticipant {
  id: string;
  contactId?: string;
  email?: string;
  displayName: string;
  status: 'pending' | 'accepted' | 'declined';
  reminders: ReminderConfig[];
}

export interface RecurrenceRule {
  frequency: RecurrenceFrequency;
  interval: number;
  byDay: number[];
  byMonthDay?: number;
  until?: string;
  count?: number;
}

export interface EventFormData {
  eventType: EventType;
  title: string;
  description: string;
  startAt: string;
  endAt: string;
  allDay: boolean;
  // Event-specific
  mode?: EventMode;
  location: string;
  meetingUrl?: string;
  recurrence?: RecurrenceRule;
  organizerReminders: ReminderConfig[];
  participants: EventParticipant[];
  // Reminder-specific
  linkedTaskId?: string;
  // Milestone-specific
  projectId: string;
  linkedTaskIds: string[];
  // Common
  contactId: string;
  color: string;
}

export interface EventFormProps {
  /** Existing event data for edit mode */
  initialData?: Partial<CalendarEvent>;
  /** Pre-fill date when creating new event */
  initialDate?: string;
  /** Pre-selected event type */
  initialEventType?: EventType;
  /** Pre-selected task (for reminder type) */
  initialTaskId?: string;
  /** Pre-selected project (for milestone type) */
  initialProjectId?: string;
  /** Called when form is submitted */
  onSubmit: (data: EventFormData) => void;
  /** Called when cancel is clicked */
  onCancel: () => void;
  /** Submit button label override */
  submitLabel?: string;
  /** Cancel button label override */
  cancelLabel?: string;
  /** Whether to show cancel button */
  showCancel?: boolean;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const PRESET_COLORS = [
  { value: '#3b82f6', labelKey: 'events.colors.blue' },
  { value: '#22c55e', labelKey: 'events.colors.green' },
  { value: '#f59e0b', labelKey: 'events.colors.orange' },
  { value: '#ef4444', labelKey: 'events.colors.red' },
  { value: '#8b5cf6', labelKey: 'events.colors.purple' },
  { value: '#ec4899', labelKey: 'events.colors.pink' },
  { value: '#06b6d4', labelKey: 'events.colors.cyan' },
  { value: '#64748b', labelKey: 'events.colors.gray' },
];

const REMINDER_OFFSETS = [
  { value: 0, labelKey: 'events.reminders.atTime' },
  { value: 5, labelKey: 'events.reminders.5min' },
  { value: 15, labelKey: 'events.reminders.15min' },
  { value: 30, labelKey: 'events.reminders.30min' },
  { value: 60, labelKey: 'events.reminders.1hour' },
  { value: 120, labelKey: 'events.reminders.2hours' },
  { value: 1440, labelKey: 'events.reminders.1day' },
  { value: 2880, labelKey: 'events.reminders.2days' },
  { value: 10080, labelKey: 'events.reminders.1week' },
];

const NOTIFICATION_CHANNELS: { value: NotificationChannel; labelKey: string; icon: string }[] = [
  { value: 'email', labelKey: 'events.channels.email', icon: '📧' },
  { value: 'whatsapp', labelKey: 'events.channels.whatsapp', icon: '💬' },
  { value: 'telegram', labelKey: 'events.channels.telegram', icon: '✈️' },
  { value: 'sms', labelKey: 'events.channels.sms', icon: '📱' },
  { value: 'push', labelKey: 'events.channels.push', icon: '🔔' },
];

const WEEKDAYS = [
  { value: 1, labelKey: 'common.days.monday' },
  { value: 2, labelKey: 'common.days.tuesday' },
  { value: 3, labelKey: 'common.days.wednesday' },
  { value: 4, labelKey: 'common.days.thursday' },
  { value: 5, labelKey: 'common.days.friday' },
  { value: 6, labelKey: 'common.days.saturday' },
  { value: 0, labelKey: 'common.days.sunday' },
];

// =============================================================================
// HELPERS
// =============================================================================

function formatDateTimeLocal(isoString: string | undefined, fallbackDate?: string): string {
  if (!isoString) {
    if (fallbackDate) {
      const now = new Date();
      const hours = now.getHours().toString().padStart(2, '0');
      const minutes = now.getMinutes().toString().padStart(2, '0');
      return `${fallbackDate}T${hours}:${minutes}`;
    }
    return '';
  }
  return isoString.slice(0, 16);
}

function getDefaultFormData(
  initialData?: Partial<CalendarEvent>,
  initialDate?: string,
  initialEventType?: EventType,
  initialTaskId?: string,
  initialProjectId?: string
): EventFormData {
  return {
    eventType: (initialData as any)?.eventType || initialEventType || 'event',
    title: initialData?.title || '',
    description: initialData?.description || '',
    startAt: formatDateTimeLocal(initialData?.startAt, initialDate),
    endAt: formatDateTimeLocal(initialData?.endAt),
    allDay: initialData?.allDay ?? false,
    mode: (initialData as any)?.mode || 'live',
    location: initialData?.location || '',
    meetingUrl: (initialData as any)?.meetingUrl || '',
    recurrence: (initialData as any)?.recurrence || undefined,
    organizerReminders: (initialData as any)?.organizerReminders || [{ channel: 'email', offsetMinutes: 30 }],
    participants: (initialData as any)?.participants || [],
    linkedTaskId: (initialData as any)?.linkedTaskId || initialTaskId || '',
    projectId: initialData?.projectId || initialProjectId || '',
    linkedTaskIds: (initialData as any)?.linkedTaskIds || [],
    contactId: initialData?.contactId || '',
    color: initialData?.color || PRESET_COLORS[0].value,
  };
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '1rem',
  },
  section: {
    background: 'var(--bg-secondary, #f9fafb)',
    borderRadius: '0.5rem',
    padding: '1rem',
    marginBottom: '0.5rem',
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontWeight: 600,
    fontSize: '0.875rem',
    marginBottom: '0.75rem',
    color: 'var(--text)',
  },
  fieldGroup: {
    marginBottom: '0.75rem',
  },
  label: {
    display: 'block',
    fontWeight: 500,
    marginBottom: '0.375rem',
    fontSize: '0.875rem',
  },
  input: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    boxSizing: 'border-box' as const,
  },
  textarea: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    resize: 'vertical' as const,
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '0.625rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    fontSize: '0.875rem',
    boxSizing: 'border-box' as const,
    background: 'white',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
  },
  gridTwo: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
  },
  gridThree: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '0.5rem',
  },
  hint: {
    display: 'block',
    fontSize: '0.75rem',
    color: 'var(--text-muted)',
    marginTop: '0.25rem',
  },
  colorPicker: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  colorButton: (isSelected: boolean, color: string) => ({
    width: '2rem',
    height: '2rem',
    borderRadius: '0.375rem',
    background: color,
    border: isSelected ? '3px solid var(--text)' : '2px solid transparent',
    cursor: 'pointer',
    transition: 'border-color 0.15s ease',
  }),
  actions: {
    display: 'flex',
    gap: '0.75rem',
    justifyContent: 'flex-end',
    marginTop: '0.5rem',
  },
  buttonCancel: {
    padding: '0.625rem 1.25rem',
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    background: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  buttonSubmit: {
    padding: '0.625rem 1.25rem',
    border: 'none',
    borderRadius: '0.375rem',
    background: 'var(--brand)',
    color: 'white',
    cursor: 'pointer',
    fontWeight: 500,
    fontSize: '0.875rem',
  },
  eventTypeSelector: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
  },
  eventTypeButton: (isSelected: boolean) => ({
    flex: 1,
    padding: '0.75rem',
    border: isSelected ? '2px solid var(--brand)' : '1px solid var(--line)',
    borderRadius: '0.5rem',
    background: isSelected ? 'var(--brand-light, #eff6ff)' : 'white',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    gap: '0.25rem',
    transition: 'all 0.15s ease',
  }),
  modeSelector: {
    display: 'flex',
    gap: '0.5rem',
  },
  modeButton: (isSelected: boolean) => ({
    flex: 1,
    padding: '0.5rem',
    border: isSelected ? '2px solid var(--brand)' : '1px solid var(--line)',
    borderRadius: '0.375rem',
    background: isSelected ? 'var(--brand-light, #eff6ff)' : 'white',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.375rem',
    fontSize: '0.875rem',
    fontWeight: isSelected ? 500 : 400,
  }),
  reminderRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '0.5rem',
    padding: '0.5rem',
    background: 'white',
    borderRadius: '0.375rem',
    border: '1px solid var(--line)',
  },
  reminderSelect: {
    flex: 1,
    padding: '0.5rem',
    border: '1px solid var(--line)',
    borderRadius: '0.25rem',
    fontSize: '0.8rem',
    background: 'white',
  },
  addButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.375rem',
    padding: '0.5rem 0.75rem',
    border: '1px dashed var(--line)',
    borderRadius: '0.375rem',
    background: 'transparent',
    cursor: 'pointer',
    fontSize: '0.8rem',
    color: 'var(--text-muted)',
  },
  removeButton: {
    padding: '0.25rem',
    border: 'none',
    background: 'transparent',
    cursor: 'pointer',
    color: 'var(--text-muted)',
    display: 'flex',
    alignItems: 'center',
  },
  participantCard: {
    padding: '0.75rem',
    background: 'white',
    borderRadius: '0.375rem',
    border: '1px solid var(--line)',
    marginBottom: '0.5rem',
  },
  participantHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.5rem',
  },
  recurrenceDetails: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    marginTop: '0.5rem',
  },
  weekdaySelector: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap' as const,
  },
  weekdayButton: (isSelected: boolean) => ({
    width: '2.5rem',
    height: '2rem',
    border: isSelected ? '2px solid var(--brand)' : '1px solid var(--line)',
    borderRadius: '0.25rem',
    background: isSelected ? 'var(--brand-light, #eff6ff)' : 'white',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: isSelected ? 600 : 400,
  }),
  taskMultiSelect: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
    maxHeight: '150px',
    overflowY: 'auto' as const,
    border: '1px solid var(--line)',
    borderRadius: '0.375rem',
    padding: '0.5rem',
  },
  taskCheckbox: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.375rem',
    fontSize: '0.875rem',
    cursor: 'pointer',
    borderRadius: '0.25rem',
  },
};

// =============================================================================
// COMPONENT
// =============================================================================

export function EventForm({
  initialData,
  initialDate,
  initialEventType,
  initialTaskId,
  initialProjectId,
  onSubmit,
  onCancel,
  submitLabel,
  cancelLabel,
  showCancel = true,
}: EventFormProps) {
  const { t } = useTranslation();
  const { projects, tasks } = useProjectsStore();
  const { contacts } = useHubStore();

  const resolvedCancelLabel = cancelLabel || t('events.cancel');
  const isEditMode = Boolean(initialData?.id);

  const [formData, setFormData] = useState<EventFormData>(() =>
    getDefaultFormData(initialData, initialDate, initialEventType, initialTaskId, initialProjectId)
  );
  const [showRecurrence, setShowRecurrence] = useState(!!formData.recurrence);

  // Filter tasks by project when in milestone mode
  const filteredTasks = useMemo(() => {
    if (formData.eventType === 'milestone' && formData.projectId) {
      return tasks.filter((task) => task.projectId === formData.projectId);
    }
    return tasks;
  }, [tasks, formData.eventType, formData.projectId]);

  // Reset form when initialData changes
  useEffect(() => {
    const newData = getDefaultFormData(initialData, initialDate, initialEventType, initialTaskId, initialProjectId);
    setFormData(newData);
    setShowRecurrence(!!newData.recurrence);
  }, [initialData, initialDate, initialEventType, initialTaskId, initialProjectId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validate based on event type
    if (formData.eventType === 'reminder' && !formData.linkedTaskId) {
      alert(t('events.validation.taskRequired'));
      return;
    }
    if (formData.eventType === 'milestone' && !formData.projectId) {
      alert(t('events.validation.projectRequired'));
      return;
    }

    onSubmit(formData);
  };

  const updateField = <K extends keyof EventFormData>(field: K, value: EventFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const addOrganizerReminder = () => {
    setFormData((prev) => ({
      ...prev,
      organizerReminders: [...prev.organizerReminders, { channel: 'email', offsetMinutes: 30 }],
    }));
  };

  const updateOrganizerReminder = (index: number, field: keyof ReminderConfig, value: any) => {
    setFormData((prev) => ({
      ...prev,
      organizerReminders: prev.organizerReminders.map((r, i) =>
        i === index ? { ...r, [field]: value } : r
      ),
    }));
  };

  const removeOrganizerReminder = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      organizerReminders: prev.organizerReminders.filter((_, i) => i !== index),
    }));
  };

  const addParticipant = () => {
    setFormData((prev) => ({
      ...prev,
      participants: [
        ...prev.participants,
        {
          id: `temp-${Date.now()}`,
          displayName: '',
          email: '',
          status: 'pending' as const,
          reminders: [{ channel: 'email', offsetMinutes: 30 }],
        },
      ],
    }));
  };

  const updateParticipant = (index: number, field: keyof EventParticipant, value: any) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.map((p, i) => (i === index ? { ...p, [field]: value } : p)),
    }));
  };

  const removeParticipant = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.filter((_, i) => i !== index),
    }));
  };

  const addParticipantReminder = (participantIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.map((p, i) =>
        i === participantIndex
          ? { ...p, reminders: [...p.reminders, { channel: 'email', offsetMinutes: 30 }] }
          : p
      ),
    }));
  };

  const updateParticipantReminder = (
    participantIndex: number,
    reminderIndex: number,
    field: keyof ReminderConfig,
    value: any
  ) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.map((p, i) =>
        i === participantIndex
          ? {
              ...p,
              reminders: p.reminders.map((r, ri) =>
                ri === reminderIndex ? { ...r, [field]: value } : r
              ),
            }
          : p
      ),
    }));
  };

  const removeParticipantReminder = (participantIndex: number, reminderIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      participants: prev.participants.map((p, i) =>
        i === participantIndex
          ? { ...p, reminders: p.reminders.filter((_, ri) => ri !== reminderIndex) }
          : p
      ),
    }));
  };

  const toggleRecurrence = () => {
    if (showRecurrence) {
      updateField('recurrence', undefined);
      setShowRecurrence(false);
    } else {
      updateField('recurrence', {
        frequency: 'weekly',
        interval: 1,
        byDay: [],
        byMonthDay: undefined,
        until: undefined,
        count: undefined,
      });
      setShowRecurrence(true);
    }
  };

  const updateRecurrence = (field: keyof RecurrenceRule, value: any) => {
    setFormData((prev) => ({
      ...prev,
      recurrence: prev.recurrence ? { ...prev.recurrence, [field]: value } : undefined,
    }));
  };

  const toggleWeekday = (day: number) => {
    if (!formData.recurrence) return;
    const currentDays = formData.recurrence.byDay || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day];
    updateRecurrence('byDay', newDays);
  };

  const toggleLinkedTask = (taskId: string) => {
    const currentIds = formData.linkedTaskIds || [];
    const newIds = currentIds.includes(taskId)
      ? currentIds.filter((id) => id !== taskId)
      : [...currentIds, taskId];
    updateField('linkedTaskIds', newIds);
  };

  // Get selected task info for Reminder type
  const selectedTask = useMemo(() => {
    if (formData.eventType !== 'reminder' || !formData.linkedTaskId) return null;
    return tasks.find((t) => t.id === formData.linkedTaskId);
  }, [formData.eventType, formData.linkedTaskId, tasks]);

  return (
    <form onSubmit={handleSubmit} style={styles.form}>
      {/* Event Type Selector */}
      <div style={styles.eventTypeSelector}>
        <button
          type="button"
          onClick={() => updateField('eventType', 'event')}
          style={styles.eventTypeButton(formData.eventType === 'event')}
        >
          <Calendar size={20} />
          <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{t('events.types.event', 'Event')}</span>
        </button>
        <button
          type="button"
          onClick={() => updateField('eventType', 'reminder')}
          style={styles.eventTypeButton(formData.eventType === 'reminder')}
        >
          <Bell size={20} />
          <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{t('events.types.reminder', 'Reminder')}</span>
        </button>
        <button
          type="button"
          onClick={() => updateField('eventType', 'milestone')}
          style={styles.eventTypeButton(formData.eventType === 'milestone')}
        >
          <Target size={20} />
          <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>{t('events.types.milestone', 'Milestone')}</span>
        </button>
      </div>

      {/* Basic Info Section */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          {formData.eventType === 'event' && <Calendar size={16} />}
          {formData.eventType === 'reminder' && <Bell size={16} />}
          {formData.eventType === 'milestone' && <Target size={16} />}
          <span>{t('events.sections.basicInfo', 'Basic Info')}</span>
        </div>

        {/* Title */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>{t('events.titleRequired')}</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => updateField('title', e.target.value)}
            required
            placeholder={t('events.titlePlaceholder')}
            style={styles.input}
          />
        </div>

        {/* Description */}
        <div style={styles.fieldGroup}>
          <label style={styles.label}>{t('events.description')}</label>
          <textarea
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            placeholder={t('events.descriptionPlaceholder')}
            rows={2}
            style={styles.textarea}
          />
        </div>

        {/* Reminder: Task Selector (Required) */}
        {formData.eventType === 'reminder' && (
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              <CheckSquare size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
              {t('events.linkedTask', 'Linked Task')} *
            </label>
            <select
              value={formData.linkedTaskId || ''}
              onChange={(e) => updateField('linkedTaskId', e.target.value)}
              style={styles.select}
              required
            >
              <option value="">{t('events.selectTask', 'Select a task...')}</option>
              {tasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.title}
                </option>
              ))}
            </select>
            {selectedTask && (
              <span style={styles.hint}>
                {t('events.taskDue', 'Due')}: {selectedTask.dueDate || t('common.notSet', 'Not set')}
              </span>
            )}
          </div>
        )}

        {/* Milestone: Project Selector (Required) */}
        {formData.eventType === 'milestone' && (
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              {t('events.project')} *
            </label>
            <select
              value={formData.projectId}
              onChange={(e) => {
                updateField('projectId', e.target.value);
                updateField('linkedTaskIds', []); // Reset selected tasks
              }}
              style={styles.select}
              required
            >
              <option value="">{t('events.selectProject', 'Select a project...')}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.title}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Milestone: Task Multi-Select */}
        {formData.eventType === 'milestone' && formData.projectId && (
          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              <CheckSquare size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
              {t('events.linkedTasks', 'Linked Tasks')}
            </label>
            <div style={styles.taskMultiSelect}>
              {filteredTasks.length === 0 ? (
                <span style={styles.hint}>{t('events.noTasksInProject', 'No tasks in this project')}</span>
              ) : (
                filteredTasks.map((task) => (
                  <label key={task.id} style={styles.taskCheckbox}>
                    <input
                      type="checkbox"
                      checked={formData.linkedTaskIds.includes(task.id)}
                      onChange={() => toggleLinkedTask(task.id)}
                    />
                    <span>{task.title}</span>
                  </label>
                ))
              )}
            </div>
            <span style={styles.hint}>
              {t('events.linkedTasksHint', 'Tasks that must be completed for this milestone')}
            </span>
          </div>
        )}
      </div>

      {/* Date/Time Section */}
      <div style={styles.section}>
        <div style={styles.sectionTitle}>
          <Calendar size={16} />
          <span>{t('events.sections.dateTime', 'Date & Time')}</span>
        </div>

        {/* All Day Checkbox */}
        <div style={styles.fieldGroup}>
          <label style={styles.checkbox}>
            <input
              type="checkbox"
              checked={formData.allDay}
              onChange={(e) => updateField('allDay', e.target.checked)}
              style={{ width: '1rem', height: '1rem' }}
            />
            <span style={{ fontWeight: 500 }}>{t('events.allDay')}</span>
          </label>
        </div>

        {/* Start & End */}
        <div style={styles.gridTwo}>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>{t('events.startRequired')}</label>
            <input
              type={formData.allDay ? 'date' : 'datetime-local'}
              value={formData.allDay ? formData.startAt.split('T')[0] : formData.startAt}
              onChange={(e) =>
                updateField(
                  'startAt',
                  formData.allDay ? `${e.target.value}T00:00` : e.target.value
                )
              }
              required
              style={styles.input}
            />
          </div>
          <div style={styles.fieldGroup}>
            <label style={styles.label}>{t('events.end')}</label>
            <input
              type={formData.allDay ? 'date' : 'datetime-local'}
              value={formData.allDay ? formData.endAt?.split('T')[0] || '' : formData.endAt}
              onChange={(e) =>
                updateField(
                  'endAt',
                  formData.allDay ? `${e.target.value}T23:59` : e.target.value
                )
              }
              style={styles.input}
            />
          </div>
        </div>
      </div>

      {/* Event-specific: Mode & Location */}
      {formData.eventType === 'event' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <MapPin size={16} />
            <span>{t('events.sections.location', 'Location & Mode')}</span>
          </div>

          {/* Mode Selector */}
          <div style={styles.fieldGroup}>
            <label style={styles.label}>{t('events.mode', 'Mode')}</label>
            <div style={styles.modeSelector}>
              <button
                type="button"
                onClick={() => updateField('mode', 'live')}
                style={styles.modeButton(formData.mode === 'live')}
              >
                <MapPin size={16} />
                {t('events.modes.live', 'In Person')}
              </button>
              <button
                type="button"
                onClick={() => updateField('mode', 'call')}
                style={styles.modeButton(formData.mode === 'call')}
              >
                <Phone size={16} />
                {t('events.modes.call', 'Call')}
              </button>
              <button
                type="button"
                onClick={() => updateField('mode', 'videocall')}
                style={styles.modeButton(formData.mode === 'videocall')}
              >
                <Video size={16} />
                {t('events.modes.videocall', 'Video Call')}
              </button>
            </div>
          </div>

          {/* Location (for live mode) */}
          {formData.mode === 'live' && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>{t('events.location')}</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => updateField('location', e.target.value)}
                placeholder={t('events.locationPlaceholder')}
                style={styles.input}
              />
            </div>
          )}

          {/* Meeting URL (for call/videocall) */}
          {(formData.mode === 'call' || formData.mode === 'videocall') && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>
                <LinkIcon size={14} style={{ display: 'inline', marginRight: '0.25rem' }} />
                {t('events.meetingUrl', 'Meeting URL')}
              </label>
              <input
                type="url"
                value={formData.meetingUrl || ''}
                onChange={(e) => updateField('meetingUrl', e.target.value)}
                placeholder="https://meet.google.com/..."
                style={styles.input}
              />
            </div>
          )}
        </div>
      )}

      {/* Event-specific: Recurrence */}
      {formData.eventType === 'event' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <Repeat size={16} />
            <span>{t('events.sections.recurrence', 'Recurrence')}</span>
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.checkbox}>
              <input
                type="checkbox"
                checked={showRecurrence}
                onChange={toggleRecurrence}
                style={{ width: '1rem', height: '1rem' }}
              />
              <span style={{ fontWeight: 500 }}>{t('events.repeats', 'Repeats')}</span>
            </label>
          </div>

          {showRecurrence && formData.recurrence && (
            <div style={styles.recurrenceDetails}>
              <div style={styles.gridTwo}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{t('events.frequency', 'Frequency')}</label>
                  <select
                    value={formData.recurrence.frequency}
                    onChange={(e) => updateRecurrence('frequency', e.target.value)}
                    style={styles.select}
                  >
                    <option value="daily">{t('events.frequencies.daily', 'Daily')}</option>
                    <option value="weekly">{t('events.frequencies.weekly', 'Weekly')}</option>
                    <option value="monthly">{t('events.frequencies.monthly', 'Monthly')}</option>
                    <option value="yearly">{t('events.frequencies.yearly', 'Yearly')}</option>
                  </select>
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{t('events.interval', 'Every')}</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.recurrence.interval}
                    onChange={(e) => updateRecurrence('interval', parseInt(e.target.value) || 1)}
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Weekday selector for weekly frequency */}
              {formData.recurrence.frequency === 'weekly' && (
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{t('events.onDays', 'On days')}</label>
                  <div style={styles.weekdaySelector}>
                    {WEEKDAYS.map((day) => (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => toggleWeekday(day.value)}
                        style={styles.weekdayButton(formData.recurrence!.byDay.includes(day.value))}
                      >
                        {t(day.labelKey, day.labelKey.split('.').pop()?.slice(0, 2) || '')}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* End condition */}
              <div style={styles.gridTwo}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{t('events.endDate', 'End date')}</label>
                  <input
                    type="date"
                    value={formData.recurrence.until || ''}
                    onChange={(e) => updateRecurrence('until', e.target.value || undefined)}
                    style={styles.input}
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{t('events.occurrences', 'Or after X times')}</label>
                  <input
                    type="number"
                    min={1}
                    value={formData.recurrence.count || ''}
                    onChange={(e) =>
                      updateRecurrence('count', e.target.value ? parseInt(e.target.value) : undefined)
                    }
                    placeholder={t('events.unlimited', 'Unlimited')}
                    style={styles.input}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Event-specific: Reminders */}
      {formData.eventType === 'event' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <Bell size={16} />
            <span>{t('events.sections.reminders', 'Your Reminders')}</span>
          </div>

          {formData.organizerReminders.map((reminder, index) => (
            <div key={index} style={styles.reminderRow}>
              <select
                value={reminder.channel}
                onChange={(e) => updateOrganizerReminder(index, 'channel', e.target.value)}
                style={styles.reminderSelect}
              >
                {NOTIFICATION_CHANNELS.map((ch) => (
                  <option key={ch.value} value={ch.value}>
                    {ch.icon} {t(ch.labelKey, ch.value)}
                  </option>
                ))}
              </select>
              <select
                value={reminder.offsetMinutes}
                onChange={(e) =>
                  updateOrganizerReminder(index, 'offsetMinutes', parseInt(e.target.value))
                }
                style={styles.reminderSelect}
              >
                {REMINDER_OFFSETS.map((offset) => (
                  <option key={offset.value} value={offset.value}>
                    {t(offset.labelKey, `${offset.value} min`)}
                  </option>
                ))}
              </select>
              <button
                type="button"
                onClick={() => removeOrganizerReminder(index)}
                style={styles.removeButton}
              >
                <X size={16} />
              </button>
            </div>
          ))}

          <button type="button" onClick={addOrganizerReminder} style={styles.addButton}>
            <Plus size={14} />
            {t('events.addReminder', 'Add Reminder')}
          </button>
        </div>
      )}

      {/* Event-specific: Participants */}
      {formData.eventType === 'event' && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>
            <Users size={16} />
            <span>{t('events.sections.participants', 'Participants')}</span>
          </div>

          {formData.participants.map((participant, pIndex) => (
            <div key={participant.id} style={styles.participantCard}>
              <div style={styles.participantHeader}>
                <strong>{participant.displayName || t('events.newParticipant', 'New Participant')}</strong>
                <button
                  type="button"
                  onClick={() => removeParticipant(pIndex)}
                  style={styles.removeButton}
                >
                  <X size={16} />
                </button>
              </div>

              <div style={styles.gridTwo}>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{t('common.name', 'Name')}</label>
                  <input
                    type="text"
                    value={participant.displayName}
                    onChange={(e) => updateParticipant(pIndex, 'displayName', e.target.value)}
                    placeholder={t('events.participantName', 'Name')}
                    style={styles.input}
                  />
                </div>
                <div style={styles.fieldGroup}>
                  <label style={styles.label}>{t('common.email', 'Email')}</label>
                  <input
                    type="email"
                    value={participant.email || ''}
                    onChange={(e) => updateParticipant(pIndex, 'email', e.target.value)}
                    placeholder="email@example.com"
                    style={styles.input}
                  />
                </div>
              </div>

              {/* Participant's reminders */}
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ ...styles.label, fontSize: '0.75rem' }}>
                  {t('events.participantReminders', 'Their Reminders')}
                </label>
                {participant.reminders.map((reminder, rIndex) => (
                  <div key={rIndex} style={{ ...styles.reminderRow, padding: '0.375rem' }}>
                    <select
                      value={reminder.channel}
                      onChange={(e) =>
                        updateParticipantReminder(pIndex, rIndex, 'channel', e.target.value)
                      }
                      style={{ ...styles.reminderSelect, fontSize: '0.75rem' }}
                    >
                      {NOTIFICATION_CHANNELS.map((ch) => (
                        <option key={ch.value} value={ch.value}>
                          {ch.icon} {t(ch.labelKey, ch.value)}
                        </option>
                      ))}
                    </select>
                    <select
                      value={reminder.offsetMinutes}
                      onChange={(e) =>
                        updateParticipantReminder(pIndex, rIndex, 'offsetMinutes', parseInt(e.target.value))
                      }
                      style={{ ...styles.reminderSelect, fontSize: '0.75rem' }}
                    >
                      {REMINDER_OFFSETS.map((offset) => (
                        <option key={offset.value} value={offset.value}>
                          {t(offset.labelKey, `${offset.value} min`)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => removeParticipantReminder(pIndex, rIndex)}
                      style={styles.removeButton}
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => addParticipantReminder(pIndex)}
                  style={{ ...styles.addButton, fontSize: '0.75rem', padding: '0.375rem 0.5rem' }}
                >
                  <Plus size={12} />
                  {t('events.addReminder', 'Add Reminder')}
                </button>
              </div>
            </div>
          ))}

          <button type="button" onClick={addParticipant} style={styles.addButton}>
            <Plus size={14} />
            {t('events.addParticipant', 'Add Participant')}
          </button>
        </div>
      )}

      {/* Color Picker */}
      <div style={styles.fieldGroup}>
        <label style={styles.label}>{t('events.color')}</label>
        <div style={styles.colorPicker}>
          {PRESET_COLORS.map((colorOption) => (
            <button
              key={colorOption.value}
              type="button"
              onClick={() => updateField('color', colorOption.value)}
              title={t(colorOption.labelKey)}
              style={styles.colorButton(formData.color === colorOption.value, colorOption.value)}
            />
          ))}
        </div>
      </div>

      {/* Contact (for all types) */}
      {formData.eventType !== 'milestone' && (
        <div style={styles.fieldGroup}>
          <label style={styles.label}>{t('events.contact')}</label>
          <select
            value={formData.contactId}
            onChange={(e) => updateField('contactId', e.target.value)}
            style={styles.select}
          >
            <option value="">{t('events.noContact')}</option>
            {contacts.map((contact) => (
              <option key={contact.id} value={contact.id}>
                {contact.displayName}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Project (only for Event type, optional) */}
      {formData.eventType === 'event' && (
        <div style={styles.fieldGroup}>
          <label style={styles.label}>{t('events.project')}</label>
          <select
            value={formData.projectId}
            onChange={(e) => updateField('projectId', e.target.value)}
            style={styles.select}
          >
            <option value="">{t('events.noProject')}</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>
                {project.title}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Actions */}
      <div style={styles.actions}>
        {showCancel && (
          <button type="button" onClick={onCancel} style={styles.buttonCancel}>
            {resolvedCancelLabel}
          </button>
        )}
        <button type="submit" style={styles.buttonSubmit}>
          {submitLabel || (isEditMode ? t('events.saveChanges') : t('events.createEvent'))}
        </button>
      </div>
    </form>
  );
}
